
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { util } from '../../../classes/util';
import { environment } from '../../../environment';
import { InputConverter } from '../../../inputConverter';
import { ExportService } from '../../../services/export.service';
import { FilterService } from '../../../services/filter.service';
import { LayerService } from '../../../services/layer.service';
import { IExportParameters, ILayer, ILayerColumn, ILayerRMS, IQuery, ITable, MapCoreLayer, QueryableMapCoreLayer } from '../../../types';
import { BaseComponent } from '../../base.component';

/** Basic Filter Component - Allows editing of a layer filter */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-layer-filter-container',
    styleUrls: ['../../sharedStyles/base.scss'],
    templateUrl: './layer-filter-container.component.html'
})
export class LayerFilterContainerComponent extends BaseComponent implements AfterViewInit {
    private _esriLayerId: string;
    private _hideExportSection: boolean;
    private _extraQuery: IQuery;
    private esriLayerId: string;
    @Input() set layerId(value: string) {
        if (this._esriLayerId !== value) {
            this.activateTab(0);
        }
        this._esriLayerId = value;
        if (this._esriLayerId) {
            this.setupFilter(value);
        }
    }
    get layerId(): string {
        return this._esriLayerId;
    }

    @Input() set extraQuery(value: IQuery) {
        this._extraQuery = value;
    }

    get extraQuery(): IQuery {
        return this._extraQuery;
    }

    @Input() @ViewChild('basicFilter') basicFilter: IFilterComponent; //: BasicFilterComponent; // todo TFS#13854
    @Input() @ViewChild('geographicFilter') geographicFilter;
    @Input() @ViewChild('joinFilter') joinFilter: IFilterComponent;
    @Input() @ViewChild('childRecordsFilter') childRecordsFilter: IFilterComponent;

    @Input() mapView: NonNullable<__esri.MapView>;

    @Input() set hideExportSection(value: boolean) {
        this._hideExportSection = value;
    }
    get hideExportSection(): boolean {
        return this._hideExportSection;
    }

    @Input()
    @InputConverter()
    public enableGeographicFilter: boolean = true;

    @Input()
    @InputConverter()
    public enableJoinFilter: boolean = true;

    @Input()
    @InputConverter()
    public enableChildRecordFilter: boolean = true;

    esriLayer: MapCoreLayer;

    loading: boolean = false;

    @Output() public readonly onapply = new EventEmitter<{ id?: string; esriLayer?: any; data?: any }>();
    @Output() public readonly ondone = new EventEmitter<void>();
    @Output() public readonly onlayerdata = new EventEmitter<ILayer>();
    @Output() public readonly onboundarysourcechanged = new EventEmitter<string>();
    layerColumns: ILayerColumn[];
    exportJoinColumns: any[];
    geometryString: string;
    filterNewLayerSub: Subscription;
    basicFilterNewLayerSub: Subscription;
    geoFilterNewLayerSub: Subscription;
    joinFilterNewLayerSub: Subscription;
    childRecordsFilterNewLayerSub: Subscription;

    filterExistingLayerSub: Subscription;
    layerDataSub: Subscription;
    basicWorkingQuerySub: Subscription;
    geometryStringSub: Subscription;
    joinWorkingQuerySub: Subscription;
    joinRecordSub: Subscription;
    childRecordSub: Subscription;
    childQuerySub: Subscription;
    exportExcelSub: Subscription;
    joinFilterExistingLayerSub: Subscription;
    childRecordsFilterExistingLayerSub: Subscription;
    primaryLayerRMSKeys: ILayerRMS;
    currentTabIndex = 0;
    showJoinFilter = true;
    showChildRecordFilter = true;
    showGeographicFilter = true;
    showGeographicParams: any;
    exportEnabled = false;
    joinFilterList: any[] = [];
    childFilterList: any[] = [];
    geographicFilterList: any[] = [];
    joinColumns: ILayerColumn[];
    childColumns: ILayerColumn[];
    constructor(_vcr: ViewContainerRef,
        private readonly layerService: LayerService,
        private readonly filterService: FilterService,
        private readonly exportService: ExportService,
        private readonly cdr: ChangeDetectorRef) {
        super(_vcr, true);

        this.registerEvent('onapply', this.onapply);
        this.registerEvent('ondone', this.ondone);
        this.registerEvent('onboundarysourcechanged', this.onboundarysourcechanged);
    }

    async ngAfterViewInit() {
        this.mapView = await this.setupMapViewRef(this.mapView);
        if (this.geographicFilter) {
            this.geographicFilter.containerElement = this.hostElement;
            this.geographicFilter.onboundarysourcechanged.subscribe(source => {
                this.onboundarysourcechanged.emit(source);
            });
        }
    }

    activateTab(index: number) {
        this.currentTabIndex = index;
        if (this.currentTabIndex === 1 && this.geographicFilter) {
            this.onboundarysourcechanged.emit(this.geographicFilter.boundarySource);
            if (!this.geographicFilter.isActivated) {
                this.geographicFilter.activate();
            }
        } else {
            this.onboundarysourcechanged.emit(null);
        }
    }
    onWorkingQueryChanged(data: any) {
        this.joinFilter.workingQuery = data;
    }
    async setupFilter(layerId: string) {
        this.loading = true;
        this.esriLayer = this.layerService.getEsriLayerByUniqueId(layerId);

        const basicFilter = this.basicFilter;
        const geographicFilter = this.geographicFilter;
        const joinFilter = this.joinFilter;
        const childRecordsFilter = this.childRecordsFilter;
        this.exportEnabled = false;
        const hideExportSection = this.hideExportSection ? this.hideExportSection : false;
        joinFilter.exportJoinColumns = [];
        joinFilter.hideExportSection = hideExportSection;
        // This is for when the layer has not been added yet.
        if (!this.esriLayer) {
            this.esriLayerId = this.layerService.getRamdonIdForEsriLayer();
            await this.processNewLayer(parseInt(this.layerId, 10), basicFilter, joinFilter, childRecordsFilter, geographicFilter);
        } else {
            this.esriLayerId = this.esriLayer.id;
            this.showChildRecordFilter = !this.esriLayer.mapcoreProps.isExternal;
            this.showJoinFilter = !this.esriLayer.mapcoreProps.isExternal;
            this.showGeographicFilter = !this.esriLayer.mapcoreProps.isExternal;

            if (this.esriLayer.mapcoreProps && this.esriLayer.mapcoreProps.exportJoinFilterParams) {
                joinFilter.exportEnabled = this.esriLayer.mapcoreProps.exportJoinFilterParams.exportEnabled;
                joinFilter.exportJoinColumns = this.esriLayer.mapcoreProps.exportJoinFilterParams.exportJoinColumns;
            } else {
                joinFilter.exportEnabled = false;
                joinFilter.exportJoinColumns = [];
            }
            if (this.esriLayer.mapcoreProps && this.esriLayer.mapcoreProps.parentId) {
                await Promise.all([this.isJoinFilter(this.esriLayer.mapcoreProps.parentId), this.isChildRecordFilter(this.esriLayer.mapcoreProps.parentId), this.isGeographicFilter(this.esriLayer.id)]);
                if (this.showJoinFilter && !joinFilter.primaryLayerRMSKeys) {
                    joinFilter.primaryLayerRMSKeys = this.primaryLayerRMSKeys;
                }
                if (this.showGeographicParams) {
                    geographicFilter.boundarySource = this.showGeographicParams.boundarySource;
                    geographicFilter.boundaryTypes = [...this.showGeographicParams.boundaryTypes];
                    geographicFilter.layerId = this.showGeographicParams.layerId;
                    geographicFilter.selectedFeatures = [...this.showGeographicParams.selectedFeatures];
                    geographicFilter.selectedCountyCode = this.showGeographicParams.selectedCountyCode;
                    geographicFilter.selectedLayerId = this.showGeographicParams.selectedLayerId;
                    geographicFilter.spatialQuerySelection = this.showGeographicParams.spatialQuerySelection;
                    geographicFilter.workingGuid = this.showGeographicParams.workingGuid;
                    geographicFilter.counties = [...this.showGeographicParams.counties];
                } else {
                    this.geographicFilter.reset();
                }
                this.cdr.detectChanges();

            }
        }

        this.setupNewSubscriptions(basicFilter, hideExportSection, joinFilter, childRecordsFilter, geographicFilter);

        if (this.esriLayer) {
            let query: IQuery = null;
            if (this.esriLayer.mapcoreProps.query) {
                query = this.esriLayer.mapcoreProps.query;
            }

            let filterLayerId = null;
            let layerColumns: ILayerColumn[] = [];

            if (!this.esriLayer.mapcoreProps.id) {
                filterLayerId = this.esriLayer.mapcoreProps.parentId;

                if (this.esriLayer.mapcoreProps.parentId === environment.layers.filter(x => x.name === 'petl')[0].id || this.esriLayer.mapcoreProps.isPETL) {
                    layerColumns = this.esriLayer.mapcoreProps.columns;
                }

                geographicFilter.esriLayers = [this.esriLayer];
            } else {
                filterLayerId = this.esriLayer.mapcoreProps.id;

                if (this.esriLayer.mapcoreProps && this.esriLayer.mapcoreProps.columns) {
                    layerColumns = this.esriLayer.mapcoreProps.columns;
                }
            }

            // If new layer and layer is an external layer
            if (!this.esriLayer.mapcoreProps.id && this.esriLayer.mapcoreProps.isExternal) {
                basicFilter.setFilter(null, this.esriLayer, util.deepCopy(layerColumns), query, layerId);
            } else {
                const externalLayerId = this.esriLayer.mapcoreProps.isExternal ? layerId : null;
                await basicFilter.setFilter(filterLayerId, this.esriLayer, util.deepCopy(layerColumns), query, externalLayerId);
                await Promise.all([
                    joinFilter.setFilter(filterLayerId, this.esriLayer, util.deepCopy(layerColumns), basicFilter.query, externalLayerId),
                    childRecordsFilter.setFilter(filterLayerId, this.esriLayer, util.deepCopy(layerColumns), basicFilter.query, externalLayerId)
                ]);
            }
        }

        this.loading = false;
        this.cdr.detectChanges();
    }

    private setupNewSubscriptions(basicFilter: IFilterComponent, hideExportSection: boolean, joinFilter: IFilterComponent, childRecordsFilter: IFilterComponent, geographicFilter: any) {
        this.clearSubs();
        if (!this.basicFilterNewLayerSub) {
            this.basicFilterNewLayerSub = basicFilter.ondone.subscribe(data => {
                if (data) {
                    if (this.exportEnabled && !hideExportSection) {
                        this.exportToExcel(data.layerId, data.query);
                    }
                    joinFilter.updateQuery(data.query);
                    childRecordsFilter.updateQuery(data.query);
                    data = this.updateDataWithParams(data);
                    if (this.geometryString) {
                        data.geometryString = this.geometryString;
                    }
                    data.esriLayerId = this.esriLayerId;
                    this.onapply.emit({ id: this.layerId, data });
                }
            });
        }
        if (!this.geoFilterNewLayerSub) {
            this.geoFilterNewLayerSub = geographicFilter.ondone.subscribe(data => {
                if (this.exportEnabled && !hideExportSection) {
                    this.exportToExcel(data.layerId, data.query);
                }
                basicFilter.updateQuery(data.query);
                childRecordsFilter.updateQuery(data.query);
                data = this.updateDataWithParams(data);
                if (this.geometryString) {
                    data.geometryString = this.geometryString;
                }
                data.esriLayerId = this.esriLayerId;
                this.isGeographicFilter(this.esriLayerId, true);
                this.onapply.emit({ id: this.layerId, data });
            });
        }
        if (!this.joinFilterNewLayerSub && this.showJoinFilter) {
            this.joinFilterNewLayerSub = joinFilter.ondone.subscribe(data => {
                if (data) {
                    if (this.exportEnabled && !hideExportSection) {
                        this.exportToExcel(data.layerId, data.query);
                    }
                    basicFilter.updateQuery(data.query);
                    childRecordsFilter.updateQuery(data.query);
                    data = this.updateDataWithParams(data);
                    if (this.geometryString) {
                        data.geometryString = this.geometryString;
                    }
                    data.esriLayerId = this.esriLayerId;
                    this.onapply.emit({ id: this.layerId, data });
                }
            });
        }
        if (!this.childRecordsFilterNewLayerSub && this.showChildRecordFilter) {
            this.childRecordsFilterNewLayerSub = childRecordsFilter.ondone.subscribe(data => {
                if (data) {
                    if (this.exportEnabled && !hideExportSection) {
                        this.exportToExcel(data.layerId, data.query);
                    }
                    joinFilter.updateQuery(data.query);
                    basicFilter.updateQuery(data.query);
                    data = this.updateDataWithParams(data);
                    if (this.geometryString) {
                        data.geometryString = this.geometryString;
                    }
                    data.esriLayerId = this.esriLayerId;
                    this.onapply.emit({ id: this.layerId, data });
                }
            });
        }
        if (!this.basicWorkingQuerySub) {
            this.basicWorkingQuerySub = basicFilter.workingQueryChange.subscribe(data => {
                if (data && data.workingQuery) {
                    if (data.workingQuery !== joinFilter.workingQuery) {
                        this.childRecordsFilter.workingQuery = util.deepCopy(data.workingQuery);
                        this.joinFilter.workingQuery = util.deepCopy(data.workingQuery);
                    }
                    this.layerColumns = data.layerColumns;
                    const summaryString = this.filterService.populateSummaryFromQuery(data.workingQuery, this.layerColumns, this.joinColumns, this.childColumns);
                    basicFilter.updateWorkingSql(summaryString);
                    joinFilter.updateWorkingSql(summaryString);
                    childRecordsFilter.updateWorkingSql(summaryString);
                }
            });
        }
        if (!this.geometryStringSub) {
            this.geometryStringSub = geographicFilter.selectedGeometryChange.subscribe(data => {
                if (data) {
                    this.geometryString = data.geometryString;
                }
            });
        }
        if (!this.joinWorkingQuerySub) {
            this.joinWorkingQuerySub = joinFilter.workingQueryChange.subscribe(data => {
                if (data && basicFilter.workingQuery && data.workingQuery) {
                    if (data.workingQuery !== basicFilter.workingQuery) {
                        this.basicFilter.workingQuery = util.deepCopy(data.workingQuery);
                        this.childRecordsFilter.workingQuery = util.deepCopy(data.workingQuery);
                    }
                    if (this.joinColumns !== data.joinColumns) {
                        this.joinColumns = data.joinColumns;
                    }
                    const summaryString = this.filterService.populateSummaryFromQuery(data.workingQuery, this.layerColumns, this.joinColumns, this.childColumns);
                    basicFilter.updateWorkingSql(summaryString);
                    joinFilter.updateWorkingSql(summaryString);
                    childRecordsFilter.updateWorkingSql(summaryString);
                }
            });
        }
        if (!this.childRecordSub) {
            this.childRecordSub = childRecordsFilter.workingQueryChange.subscribe(data => {
                if (data && basicFilter.workingQuery && data.workingQuery) {
                    if (data.workingQuery !== basicFilter.workingQuery) {
                        this.basicFilter.workingQuery = data.workingQuery;
                        this.joinFilter.workingQuery = data.workingQuery;
                    }
                    if (this.childColumns !== data.childColumns) {
                        this.childColumns = data.childColumns;
                    }
                    const summaryString = this.filterService.populateSummaryFromQuery(data.workingQuery, this.layerColumns, this.joinColumns, this.childColumns);
                    basicFilter.updateWorkingSql(summaryString);
                    joinFilter.updateWorkingSql(summaryString);
                    childRecordsFilter.updateWorkingSql(summaryString);
                }
            });
        }
        if (!this.filterExistingLayerSub) {
            this.filterExistingLayerSub = basicFilter.ondone.subscribe((data) => {
                this.updateMapcoreProps(data);
            });
        }
        if (!this.joinFilterExistingLayerSub) {
            this.joinFilterExistingLayerSub = joinFilter.ondone.subscribe((data) => {
                this.updateMapcoreProps(data);
            });
        }
        if (!this.childRecordsFilterExistingLayerSub) {
            this.childRecordsFilterExistingLayerSub = childRecordsFilter.ondone.subscribe((data) => {
                this.updateMapcoreProps(data);
            });
        }
        if (!this.exportExcelSub) {
            this.exportExcelSub = joinFilter.exportOptionChange.subscribe((data) => {
                if (data) {
                    this.exportEnabled = data.exportEnabled;
                    this.layerColumns = data.layerColumns;
                    this.exportJoinColumns = data.exportJoinColumns;

                }
            });
        }
        if (!this.layerDataSub) {
            this.layerDataSub = basicFilter.onlayerdata.subscribe(data => this.onlayerdata.emit(data));
        }
    }

    private clearSubs() {
        if (this.basicFilterNewLayerSub) {
            this.basicFilterNewLayerSub.unsubscribe();
            this.basicFilterNewLayerSub = null;
        }
        if (this.geoFilterNewLayerSub) {
            this.geoFilterNewLayerSub.unsubscribe();
            this.geoFilterNewLayerSub = null;
        }
        if (this.joinFilterNewLayerSub) {
            this.joinFilterNewLayerSub.unsubscribe();
            this.joinFilterNewLayerSub = null;
        }
        if (this.childRecordsFilterNewLayerSub) {
            this.childRecordsFilterNewLayerSub.unsubscribe();
            this.childRecordsFilterNewLayerSub = null;
        }
        if (this.basicWorkingQuerySub) {
            this.basicWorkingQuerySub.unsubscribe();
            this.basicWorkingQuerySub = null;
        }
        if (this.joinWorkingQuerySub) {
            this.joinWorkingQuerySub.unsubscribe();
            this.joinWorkingQuerySub = null;
        }
        if (this.childRecordSub) {
            this.childRecordSub.unsubscribe();
            this.childRecordSub = null;
        }
        if (this.filterExistingLayerSub) {
            this.filterExistingLayerSub.unsubscribe();
            this.filterExistingLayerSub = null;
        }
        if (this.joinFilterExistingLayerSub) {
            this.joinFilterExistingLayerSub.unsubscribe();
            this.joinFilterExistingLayerSub = null;
        }
        if (this.childRecordsFilterExistingLayerSub) {
            this.childRecordsFilterExistingLayerSub.unsubscribe();
            this.childRecordsFilterExistingLayerSub = null;
        }
        if (this.exportExcelSub) {
            this.exportExcelSub.unsubscribe();
            this.exportExcelSub = null;
        }
        if (this.layerDataSub) {
            this.layerDataSub.unsubscribe();
            this.layerDataSub = null;
        }
    }

    private async processNewLayer(layerId: number, basicFilter: any, joinFilter: any, childRecordsFilter: any, geographicFilter: any) {
        await Promise.all([this.isJoinFilter(layerId), this.isChildRecordFilter(layerId)]);
        if (!this.extraQuery) {
            basicFilter.query = {};
        } else {
            const extraFilters = this.extraQuery.subqueries.filter(x => x.field === 'MAINT_FY' || x.field === 'CALENDAR_YEAR');
            if (extraFilters.length > 0) {
                basicFilter.extraFilters = extraFilters;
                basicFilter.query = {};
            } else {
                basicFilter.query = this.extraQuery;
            }
        }
        basicFilter.layerId = layerId;
        basicFilter.esriLayer = null;
        if (this.showJoinFilter) {
            joinFilter.primaryLayerRMSKeys = this.primaryLayerRMSKeys;
            basicFilter.query.joinTableQuery = {};
            joinFilter.query = basicFilter.query;
            joinFilter.layerId = layerId;
        }
        if (this.showChildRecordFilter) {
            basicFilter.query.childTableQuery = {};
            childRecordsFilter.query = basicFilter.query;
            childRecordsFilter.layerId = layerId;
        }
        this.geographicFilter.reset();
        geographicFilter.layerId = layerId;
        geographicFilter.esriLayerId = this.esriLayerId;
        geographicFilter.esriLayers = [];
        this.cdr.detectChanges();
    }

    private updateMapcoreProps(data: any) {
        try {
            if (this.esriLayer && data) {
                this.esriLayer.mapcoreProps.filter = data.sql;
                this.esriLayer.mapcoreProps.query = data.query;
                this.esriLayer.mapcoreProps.joinColumns = this.joinColumns;
                this.esriLayer.mapcoreProps.childColumns = this.childColumns;
                this.esriLayer.mapcoreProps.exportJoinFilterParams = { exportEnabled: this.exportEnabled };
                if (this.esriLayer.mapcoreProps.exportJoinFilterParams) {
                    this.esriLayer.mapcoreProps.exportJoinFilterParams.exportEnabled = this.exportEnabled;
                    this.esriLayer.mapcoreProps.exportJoinFilterParams.exportJoinColumns = this.exportJoinColumns;
                }
                this.layerService.updateLayerFilter(this.esriLayer as QueryableMapCoreLayer, data.query);
                this.onapply.emit({ esriLayer: this.esriLayer });
            } else {
                this.ondone.emit();
            }
        } catch { }
    }

    async isJoinFilter(layerId: number) {
        const cacheShowFilter = this.joinFilterList.filter(x => x.layerId === layerId);
        if (cacheShowFilter && cacheShowFilter.length > 0) {
            this.showJoinFilter = cacheShowFilter[0].show;
            if (this.showJoinFilter) {
                this.primaryLayerRMSKeys = cacheShowFilter[0].rmsKeys;
            }
        } else {
            const layers = await this.filterService.getLayerData(layerId);
            const tables = layers[0].tables;
            const table: ITable = tables.sort((a, b) => a.geometryType.localeCompare(b.geometryType))[0];
            const result = table ? await this.layerService.getTableRMSKeysByName(`${table.owner}.${table.name}`) : null;
            this.showJoinFilter = (result !== null);
            if (result && result.offsetEndColName) {
                result.isOffsetNumber = this.filterService.offSetIsNumber(result.offsetEndColName, layers[0].columns);
            }
            this.primaryLayerRMSKeys = result;
            this.joinFilterList.push({ layerId, show: this.showJoinFilter, rmsKeys: result });
        }
    }
    async isChildRecordFilter(layerId: number) {
        const cacheShowFilter = this.childFilterList.filter(x => x.layerId === layerId);
        if (cacheShowFilter && cacheShowFilter.length > 0) {
            this.showChildRecordFilter = cacheShowFilter[0].show;
        } else {
            const relationships = await this.layerService.getLayerChildTableRelationships(layerId);
            this.showChildRecordFilter = (relationships !== null && !!relationships.length);
            this.childFilterList.push({ layerId, show: this.showChildRecordFilter });
        }
    }

    async isGeographicFilter(layerId: number | string, overwrite = false) {
        const cacheShowFilter = this.geographicFilterList.filter(x => x.layerId === layerId);
        if (cacheShowFilter && cacheShowFilter.length > 0) {
            if (overwrite) {
                cacheShowFilter[0].show = { ...this.geographicFilter };
            } else {
                this.showGeographicParams = cacheShowFilter[0].show;
            }
        } else if (this.geographicFilter.boundaryTypes && this.geographicFilter.boundaryTypes.length && this.geographicFilter.boundaryTypes.length > 0) {
            this.showGeographicParams = { ...this.geographicFilter };
            this.geographicFilterList.push({ layerId, show: this.showGeographicParams });
        }
    }

    async exportToExcel(layerId: number, query: IQuery) {
        if (this.exportEnabled) {
            //export to excel
            const exportParameters: IExportParameters = {
                layerId,
                query,
                title: 'export'

            };
            exportParameters.fieldNames = this.layerColumns.map((x) => x.name);
            exportParameters.joinFieldNames = this.exportJoinColumns.map((x) => x.name);
            const columns: ILayerColumn[] = [];
            this.exportJoinColumns.forEach((joinColumn) => {
                const col: ILayerColumn = {
                    alias: `${joinColumn.name}_1`,
                    name: joinColumn.name,
                    databaseDataType: joinColumn.dataType,
                    databaseColumnOrder: 0,
                    isCommaSeparated: false
                };
                columns.push(col);
            });
            exportParameters.joinColumns = columns;
            await this.exportService.exportToExcelAndOpenUrl(exportParameters);

        }
    }
    updateDataWithParams(data) {
        if (this.joinColumns) {
            data.joinColumns = this.joinColumns;
        }
        if (this.childColumns) {
            data.childColumns = this.childColumns;
        }
        data.exportJoinFilterParams = { exportEnabled: this.exportEnabled };
        if (this.exportEnabled) {
            data.exportEnabled = this.exportEnabled;
            data.exportJoinColumns = this.exportJoinColumns;
        }
        if (this.geometryString) {
            data.geometryString = this.geometryString;
        }
        return data;
    }
}

interface IFilterComponent {
    [key: string]: any;
    setFilter: (layerId: number, esriLayer: MapCoreLayer, layerColumns: ILayerColumn[], query: IQuery, externalLayerId?: string) => Promise<void>;
}
